import { AxiosError } from "axios";
import {
  RequestSiterefCreateRequest,
  APIError,
  OutbrainCredentials,
  TaboolaCredentials,
} from "@/backend/types/backendTypes";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../useAuth";
import { getDefaultStore } from "jotai";
import { posthogFeatureFlagsAtom } from "@/atoms";
import { DEPLOY_ENVIRONMENT } from "@/constants/constants";
import axiosInstance from "@/lib/axios";

export const useRequestCreateSiteref = () => {
  const queryClient = useQueryClient();
  const { data } = useAuth();

  return useMutation({
    mutationFn: async ({
      organisationId,
      type,
      credentials,
      featureFlagOverride,
    }: RequestSiterefCreateRequest) => {
      if (
        [
          "tiktok",
          "fb",
          "google",
          "pinterest",
          "outbrain",
          "msads",
          "taboola",
        ].indexOf(type) < 0
      ) {
        throw new Error(
          `This account type (${type}) is currently not supported`
        );
      }
      if (!organisationId) {
        throw new Error(
          "You must select an organisation to connect an ad account."
        );
      }

      try {
        if (type === "outbrain") {
          const outbrainCredentials = credentials as OutbrainCredentials;
          if (!outbrainCredentials?.email || !outbrainCredentials?.password) {
            throw new Error(
              "Email and password are required for Outbrain connection"
            );
          }
          const headers: Record<string, string> = {
            "X-TRACIFY-ENV": DEPLOY_ENVIRONMENT,
          };

          if (featureFlagOverride) {
            headers["X-TRACIFY-FEATUREFLAGS"] = featureFlagOverride;
          } else {
            const featureFlags = await getDefaultStore().get(
              posthogFeatureFlagsAtom
            );
            if (featureFlags.flags?.outbrain_account_setup?.header_value) {
              headers["X-TRACIFY-FEATUREFLAGS"] =
                featureFlags.flags?.outbrain_account_setup?.header_value;
            }
          }

          const response = await axiosInstance.post(
            `${process.env.NEXT_PUBLIC_AD_CONNECTOR_V1_BASE_URL}/outbrain/credentials`,
            {
              organization_id: organisationId,
              email: outbrainCredentials.email,
              password: outbrainCredentials.password,
            },
            {
              headers,
            }
          );

          if (response.status !== 200) {
            throw new Error(response.data.error);
          }

          return response.data.result;
        } else if (type === "taboola") {
          const taboolaCredentials = credentials as TaboolaCredentials;
          if (
            !taboolaCredentials?.client_id ||
            !taboolaCredentials?.client_secret
          ) {
            throw new Error(
              "Client ID and Client Secret are required for Taboola connection"
            );
          }
          const headers: Record<string, string> = {
            Accept: "application/json",
            "Content-Type": "application/json",
            "tracify-token": data?.token ?? "",
            "X-TRACIFY-ENV": DEPLOY_ENVIRONMENT,
          };

          const response = await axiosInstance.post(
            `${process.env.NEXT_PUBLIC_AD_CONNECTOR_V1_BASE_URL}/taboola/credentials`,
            {
              organization_id: organisationId,
              client_id: taboolaCredentials.client_id,
              client_secret: taboolaCredentials.client_secret,
            },
            {
              headers,
            }
          );

          if (response.status !== 200) {
            throw new Error(response.data.error);
          }

          return response.data.result;
        } else {
          const formData = new FormData();
          formData.append("organisation_id", organisationId);

          const response = await axiosInstance.post(
            `https://adconnector.tracify.ai/onboarding/${
              type !== "fb" ? type : "facebook"
            }/connect/`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
              withCredentials: true,
            }
          );

          if (response.status !== 200) {
            throw new Error(response.data.error);
          }

          const redirect_to = response.data.redirect_to;
          window.location.href = redirect_to;
          // Return a never-resolving promise to keep mutation in pending state
          // until browser navigation completes - prevents onSuccess from firing
          return new Promise(() => {});
        }
      } catch (err: any) {
        if (
          err &&
          typeof err === "object" &&
          "isAxiosError" in err &&
          err.isAxiosError
        ) {
          const axiosError = err as AxiosError;
          if (!axiosError.response) {
            throw err;
          }

          // Handle specific Outbrain error cases
          if (type === "outbrain") {
            if (axiosError.response?.status === 401) {
              throw new Error(
                "Invalid Outbrain credentials. Please check your email and password and make sure you have an Amplify Api Token."
              );
            } else if (axiosError.response?.status === 409) {
              throw new Error(
                "Service temporarily unavailable. Please try again later."
              );
            } else if (axiosError.response?.status === 400) {
              const apiError = axiosError.response?.data as APIError;
              throw new Error(
                apiError?.detail ??
                  "Invalid request. Please check your email and password."
              );
            }
          }

          // Handle specific Taboola error cases
          if (type === "taboola") {
            if (axiosError.response?.status === 401) {
              throw new Error(
                "Invalid Taboola credentials. Please check your Client ID and Client Secret."
              );
            } else if (axiosError.response?.status === 409) {
              throw new Error(
                "Service temporarily unavailable. Please try again later."
              );
            }
          }

          const apiError = axiosError.response?.data as APIError & {
            detail?: string;
          };
          throw new Error(
            apiError?.detail ?? apiError?.error ?? axiosError.message
          );
        }
        throw new Error("Unknown error occurred!");
      }
    },
    onSuccess: (data, values) => {
      queryClient.invalidateQueries({ queryKey: ["adAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["connectedAdAccounts"] });
      if (values.type === "outbrain") {
        queryClient.invalidateQueries({
          queryKey: ["outbrainCredentials", values.organisationId],
        });
      }
      queryClient.invalidateQueries({
        queryKey: [
          "adAccountsByOrganisation",
          values.organisationId,
          values.type,
        ],
      });
      if (values.type === "taboola") {
        queryClient.invalidateQueries({
          queryKey: ["taboolaCredentials", values.organisationId],
        });
      }
    },
  });
};
